import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Autocomplete } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { fetchRawGoldIssuedReport, fetchUniqueBatches } from '../../api/stockIssue/stockIssueApi';
import { fetchEmployees } from '../../api/employee/employeeApi';
import { fetchRawStockCodes } from '../../api/rawStockCode/rawStockCodeApi';
import { fetchStocks } from '../../api/stock/stockApi';
import { fetchPurities } from '../../api/purity/purityApi';
import { fetchColors } from '../../api/color/colorApi';
import { fetchStones } from '../../api/stone/stoneApi';
import RawGoldIssuedReportTable from '../../components/report/RawGoldIssuedReportTable';
import ReportTitle from '../../components/common/ReportTitle';
import PageLoader from '../../components/common/PageLoader';

const DEFAULT_LIMIT = 200;

const initialFilters = {
  date_from: '',
  date_to: '',
  emp_code: '',
  batch_no: '',
  stock_code: '',
  stock_name: '',
  stock_type: '',
  karatage: '',
  color: '',
  stone_type: '',
  today_only: true,
  limit: DEFAULT_LIMIT
};

const formatCsvData = (data) => {
  const header = [
    'Date', 'Emp Code', 'Stock Code', 'Stock Name', 'Stock Type', 'Karatage', 'Color', 'Stone Type', 'Qty', 'Weight', 'Pure Weight', 'Weight CTS'
  ];
  const rows = data.map(row => [
    row.date,
    row.emp_code,
    row.stock_code,
    row.stock_name,
    row.stock_type,
    row.karatage,
    row.color,
    row.stone_type,
    row.qty,
    row.weight,
    row.pure_weight,
    row.weight_cts
  ].join(','));
  return [header.join(','), ...rows].join('\n');
};

const RawGoldIssuedReportPage = () => {
  const normalizeFilters = (input = {}) => {
    const merged = {
      ...initialFilters,
      ...input
    };

    const hasDateRange = Boolean(merged.date_from) || Boolean(merged.date_to);

    if (hasDateRange) {
      merged.today_only = input.today_only !== undefined ? input.today_only : false;
      merged.limit = input.limit !== undefined ? input.limit : DEFAULT_LIMIT;
    } else {
      merged.today_only = input.today_only !== undefined ? input.today_only : true;
      merged.limit = input.limit !== undefined ? input.limit : DEFAULT_LIMIT;
    }

    return merged;
  };

  const [filters, setFilters] = useState(initialFilters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(filters);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_qty: 0, total_weight: 0, total_pure_weight: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Master data for dropdowns
  const [employees, setEmployees] = useState([]);
  const [stockCodes, setStockCodes] = useState([]);
  const [stockTypes, setStockTypes] = useState([]);
  const [purities, setPurities] = useState([]);
  const [colors, setColors] = useState([]);
  const [stoneTypes, setStoneTypes] = useState([]);
  const [batchNumbers, setBatchNumbers] = useState([]);

  useEffect(() => {
    fetchEmployees().then(res => setEmployees(res.data)).catch(() => setEmployees([]));
    fetchRawStockCodes().then(res => setStockCodes(res.data)).catch(() => setStockCodes([]));
    fetchStocks().then(res => setStockTypes(res.data)).catch(() => setStockTypes([]));
    fetchPurities().then(res => setPurities(res.data)).catch(() => setPurities([]));
    fetchColors().then(res => setColors(res.data)).catch(() => setColors([]));
    fetchStones().then(res => setStoneTypes(res.data)).catch(() => setStoneTypes([]));
    fetchUniqueBatches().then(res => setBatchNumbers(res.data)).catch(() => setBatchNumbers([]));
  }, []);

  useEffect(() => {
    const normalized = normalizeFilters(filters);
    setLoading(true);
    fetchRawGoldIssuedReport(normalized)
      .then(res => {
        setRows(res.data.rows);
        setTotals(res.data.totals);
        setError(null);
      })
      .catch(err => {
        setRows([]);
        setTotals({ total_qty: 0, total_weight: 0, total_pure_weight: 0 });
        setError(err.message || 'Error fetching report');
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const handleOpenFilterDialog = () => {
    setPendingFilters(filters);
    setFilterDialogOpen(true);
  };
  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };
  const handlePendingInputChange = (e) => {
    setPendingFilters({ ...pendingFilters, [e.target.name]: e.target.value });
  };
  const handleApplyFilters = () => {
    setFilters(normalizeFilters(pendingFilters));
    setFilterDialogOpen(false);
  };
  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPendingFilters(initialFilters);
    setFilterDialogOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.date_from) count++;
    if (filters.date_to) count++;
    if (filters.emp_code) count++;
    if (filters.stock_code) count++;
    if (filters.stock_name) count++;
    if (filters.stock_type) count++;
    if (filters.karatage) count++;
    if (filters.color) count++;
    if (filters.stone_type) count++;
    if (filters.batch_no) count++;
    return count;
  };

  const handleLoadRecent = () => {
    setFilters(initialFilters);
  };

  const handleLoadAll = () => {
    setFilters(prev => normalizeFilters({
      ...prev,
      today_only: false,
      limit: 0
    }));
  };

  const handleExport = () => {
    const csvData = formatCsvData(rows);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'raw_gold_issued_report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <PageLoader loading={loading} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <ReportTitle>Raw Gold Issued Report</ReportTitle>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FilterListIcon />}
          onClick={handleOpenFilterDialog}
          sx={{ 
            mr: 2,
            position: 'relative',
            ...(getActiveFiltersCount() > 0 && {
              backgroundColor: 'primary.light',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.main',
              }
            })
          }}
        >
          Filters
          {getActiveFiltersCount() > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: 'error.main',
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
            >
              {getActiveFiltersCount()}
            </Box>
          )}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleLoadRecent}
          disabled={loading}
        >
          Load Recent
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleLoadAll}
          disabled={loading}
        >
          Load All
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
        </Box>
      </Box>
      <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Filter Raw Gold Issued Report</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseFilterDialog}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.emp_code} - ${option.name || option.emp_name || 'N/A'}`}
                value={employees.find(e => e.emp_code === pendingFilters.emp_code) || null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, emp_code: newValue ? newValue.emp_code : '' });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Employee" 
                    variant="outlined"
                    placeholder="Select employee..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={stockCodes}
                getOptionLabel={(option) => `${option.stock_code} - ${option.stock_name || 'N/A'}`}
                value={stockCodes.find(sc => sc.stock_code === pendingFilters.stock_code) || null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, stock_code: newValue ? newValue.stock_code : '' });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Stock Code" 
                    variant="outlined"
                    placeholder="Select stock code..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <TextField
                label="Stock Name"
                name="stock_name"
                value={pendingFilters.stock_name}
                onChange={handlePendingInputChange}
                fullWidth
                variant="outlined"
                placeholder="Enter stock name..."
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={stockTypes}
                getOptionLabel={(option) => option.name || option.stock_type || 'N/A'}
                value={stockTypes.find(s => s.name === pendingFilters.stock_type || s.stock_type === pendingFilters.stock_type) || null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, stock_type: newValue ? (newValue.name || newValue.stock_type) : '' });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Stock Type" 
                    variant="outlined"
                    placeholder="Select stock type..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={purities}
                getOptionLabel={(option) => option.name || option.purity || 'N/A'}
                value={purities.find(p => p.name === pendingFilters.karatage || p.purity === pendingFilters.karatage) || null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, karatage: newValue ? (newValue.name || newValue.purity) : '' });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Karatage/Purity" 
                    variant="outlined"
                    placeholder="Select karatage..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={colors}
                getOptionLabel={(option) => option.name || option.color || 'N/A'}
                value={colors.find(c => c.name === pendingFilters.color || c.color === pendingFilters.color) || null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, color: newValue ? (newValue.name || newValue.color) : '' });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Color" 
                    variant="outlined"
                    placeholder="Select color..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={stoneTypes}
                getOptionLabel={(option) => option.name || option.stone_type || 'N/A'}
                value={stoneTypes.find(s => s.name === pendingFilters.stone_type || s.stone_type === pendingFilters.stone_type) || null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, stone_type: newValue ? (newValue.name || newValue.stone_type) : '' });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Stone Type" 
                    variant="outlined"
                    placeholder="Select stone type..."
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={batchNumbers}
                getOptionLabel={(option) => option || ''}
                value={pendingFilters.batch_no ? batchNumbers.find(b => b === pendingFilters.batch_no) || pendingFilters.batch_no : null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, batch_no: newValue || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Batch No"
                    variant="outlined"
                    placeholder="Select batch no..."
                  />
                )}
                fullWidth
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <TextField
                label="From Date"
                type="date"
                name="date_from"
                value={pendingFilters.date_from}
                onChange={handlePendingInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
              <TextField
                label="To Date"
                type="date"
                name="date_to"
                value={pendingFilters.date_to}
                onChange={handlePendingInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
          >
            Clear All
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyFilters}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Qty</Typography>
              <Typography variant="h6" color="inherit">{totals.total_qty}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Weight</Typography>
              <Typography variant="h6" color="inherit">{Number(totals.total_weight).toFixed(3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Pure Weight</Typography>
              <Typography variant="h6" color="inherit">{Number(totals.total_pure_weight).toFixed(3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <RawGoldIssuedReportTable reportData={rows} loading={loading} error={error} />
    </Box>
  );
};

export default RawGoldIssuedReportPage; 